import React, { useEffect, useState } from "react";
import tokenService from "../../../_services/token.service"; // 👈 add this

const STATUS_COLORS = {
    ok: "#2e7d32",
    degraded: "#f9a825",
    down: "#c62828",
};

const CHECK_STATUS_COLORS = {
    ok: "#2e7d32",
    warning: "#f9a825",
    error: "#c62828",
};

function StatusBadge({ status }) {
    const color = STATUS_COLORS[status] || "#757575";
    const label =
        status === "ok"
            ? "Todos los sistemas operativos"
            : status === "degraded"
                ? "Degradado"
                : status === "down"
                    ? "Algunos sistemas caídos"
                    : "Desconocido";

    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "8px 12px",
                borderRadius: "999px",
                backgroundColor: "#f5f5f5",
                border: `1px solid ${color}`,
                gap: "8px",
            }}
        >
            <span
                style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: color,
                }}
            />
            <span style={{ color, fontWeight: 600 }}>{label}</span>
        </div>
    );
}

function CheckRow({ check }) {
    const color = CHECK_STATUS_COLORS[check.status] || "#757575";
    const icon =
        check.status === "ok"
            ? "●"
            : check.status === "warning"
                ? "▲"
                : check.status === "error"
                    ? "✖"
                    : "●";
    const statusLabel =
        check.status === "ok"
            ? "OK"
            : check.status === "warning"
                ? "ADVERTENCIA"
                : check.status === "error"
                    ? "ERROR"
                    : check.status.toUpperCase();

    return (
        <tr>
            <td style={{ padding: "8px 12px", fontWeight: 500 }}>{check.name}</td>
            <td style={{ padding: "8px 12px" }}>
                <span style={{ color, fontWeight: 600 }}>
                    {icon} {statusLabel}
                </span>
            </td>
            <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                {check.latency_ms != null ? `${check.latency_ms} ms` : "—"}
            </td>
            <td style={{ padding: "8px 12px", fontSize: 13, color: "#555" }}>
                <div>{check.message}</div>
                {check.registered_at && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#616161" }}>
                        <div>
                            Fecha:{" "}
                            {new Date(check.registered_at).toLocaleString(undefined, {
                                dateStyle: "short",
                                timeStyle: "short",
                            })}
                        </div>
                        {(check.subject_name || check.subject_email) && (
                            <div>
                                {check.subject_name || "—"}
                                {check.subject_email ? ` (${check.subject_email})` : ""}
                            </div>
                        )}
                        {check.admin_url && (
                            <a
                                href={check.admin_url}
                                style={{ color: "#1565c0", textDecoration: "none" }}
                            >
                                {check.admin_label || "Ver en superadmin"}
                            </a>
                        )}
                    </div>
                )}
            </td>
        </tr>
    );
}

function ChecksTable({ checks }) {
    return (
        <div
            style={{
                borderRadius: 8,
                border: "1px solid #e0e0e0",
                overflow: "hidden",
                backgroundColor: "#fff",
            }}
        >
            <table
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 14,
                }}
            >
                <thead style={{ backgroundColor: "#fafafa" }}>
                <tr>
                    <th
                        style={{
                            textAlign: "left",
                            padding: "8px 12px",
                            borderBottom: "1px solid #e0e0e0",
                        }}
                    >
                        Servicio
                    </th>
                    <th
                        style={{
                            textAlign: "left",
                            padding: "8px 12px",
                            borderBottom: "1px solid #e0e0e0",
                        }}
                    >
                        Estado
                    </th>
                    <th
                        style={{
                            textAlign: "left",
                            padding: "8px 12px",
                            borderBottom: "1px solid #e0e0e0",
                        }}
                    >
                        Latencia
                    </th>
                    <th
                        style={{
                            textAlign: "left",
                            padding: "8px 12px",
                            borderBottom: "1px solid #e0e0e0",
                        }}
                    >
                        Detalles
                    </th>
                </tr>
                </thead>
                <tbody>
                {checks.map((check) => (
                    <CheckRow key={check.id} check={check} />
                ))}
                </tbody>
            </table>
        </div>
    );
}

function computeOverallStatusFromCritical(checks, fallback = "ok") {
    const criticalChecks = (checks || []).filter((check) => check.critical);
    if (!criticalChecks.length) return fallback;
    if (criticalChecks.some((check) => check.status === "error")) return "down";
    if (criticalChecks.some((check) => check.status === "warning")) return "degraded";
    return "ok";
}

export default function StatusPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadStatus = () => {
        setLoading(true);
        setError(null);

        const token = tokenService.getLocalAccessToken();
        // if your API base lives in config.SERVER_URL (like other admin calls):
        // const url = `${config.SERVER_URL}admin/status/`;
        // if you mounted it at /api/admin/status/ on the same host:
        const url = "/api/admin/status/";

        fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
            .then(async (res) => {
                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(`HTTP ${res.status}: ${text}`);
                }
                return res.json();
            })
            .then((json) => {
                setData(json);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Status error", err);
                setError(err.message);
                setLoading(false);
            });
    };

    useEffect(() => {
        loadStatus();
    }, []);

    const criticalChecks = (data?.checks || []).filter((check) => check.critical);
    const businessChecks = (data?.checks || []).filter((check) => !check.critical);
    const uiOverallStatus = computeOverallStatusFromCritical(
        data?.checks,
        data?.overall_status
    );

    return (
        <div style={{ padding: 24 }}>
            <div
                style={{
                    marginBottom: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                }}
            >
                <div>
                    <h1 style={{ margin: 0, fontSize: 24 }}>Estado del sistema</h1>
                    <p style={{ marginTop: 4, color: "#616161" }}>
                        El estado general se calcula solo con infraestructura crítica.
                    </p>
                </div>
                <button
                    onClick={loadStatus}
                    disabled={loading}
                    style={{
                        padding: "8px 16px",
                        borderRadius: 6,
                        border: "1px solid #1976d2",
                        backgroundColor: loading ? "#e3f2fd" : "#1976d2",
                        color: loading ? "#1976d2" : "#fff",
                        fontWeight: 600,
                        cursor: loading ? "default" : "pointer",
                    }}
                >
                    {loading ? "Comprobando..." : "Volver a ejecutar pruebas"}
                </button>
            </div>

            {error && (
                <div
                    style={{
                        marginBottom: 16,
                        padding: 12,
                        borderRadius: 4,
                        backgroundColor: "#ffebee",
                        color: "#c62828",
                    }}
                >
                    No se pudo cargar el estado: {error}
                </div>
            )}

            {data && (
                <>
                    <div style={{ marginBottom: 24 }}>
                        <StatusBadge status={uiOverallStatus} />
                        <div style={{ marginTop: 8, fontSize: 12, color: "#757575" }}>
                            Última revisión:{" "}
                            {new Date(data.last_checked).toLocaleString(undefined, {
                                dateStyle: "short",
                                timeStyle: "medium",
                            })}
                        </div>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <h2 style={{ margin: "0 0 4px 0", fontSize: 18 }}>
                            Infraestructura crítica
                        </h2>
                        <p style={{ margin: "0 0 12px 0", color: "#616161", fontSize: 13 }}>
                            Estos checks determinan el estado general (BD y workers).
                        </p>
                        <ChecksTable checks={criticalChecks} />
                    </div>

                    <div>
                        <h2 style={{ margin: "0 0 4px 0", fontSize: 18 }}>
                            Comprobaciones de salud del negocio
                        </h2>
                        <p style={{ margin: "0 0 12px 0", color: "#616161", fontSize: 13 }}>
                            Métricas operativas y calidad de datos. No impactan el estado general.
                        </p>
                        <ChecksTable checks={businessChecks} />
                    </div>
                </>
            )}

            {loading && !data && <p>Cargando estado...</p>}
        </div>
    );
}
